import { useCallback, useEffect, useReducer, useRef } from "react";
import type { ChoiceOut, TrainingItemOut } from "../api/types";
import type { DrillOutcome } from "./useDrillSession";

// ---------------------------------------------------------------------------
// Fast multiple-choice drill state machine.
//
// Each puzzle runs through four phases:
//   preview  — reveal candidate moves on the board one at a time (arrow 1, then
//              1+2, then 1+2+3). The host renders `revealedCount` arrows.
//   choosing — all three buttons live; a per-puzzle countdown ticks down. The
//              user taps one option, or the clock hits zero (a timeout = fail).
//   feedback — brief pause showing the result (picked option + correct option).
//   (advance)— host calls `next()`; the machine re-inits at the next item or
//              completes.
//
// Like `useDrillSession` this is platform-pure: the host supplies the clock via
// `nowMs` and dispatches `TICK`, so the reducer is deterministic + unit-testable.
// It reuses `DrillOutcome` so the existing markSolved/complete hooks plug in
// unchanged (tries_used is always 1 — choice drills are single-shot).
// ---------------------------------------------------------------------------

export type ChoicePhase = "preview" | "choosing" | "feedback";

export interface ChoiceDrillState {
  index: number;
  items: TrainingItemOut[];
  phase: ChoicePhase;
  /** How many candidate arrows are revealed (0..3) during preview. */
  revealedCount: number;
  /** Seconds left to choose; null until the choosing phase starts. */
  remainingSec: number | null;
  /** The option the user picked (null on timeout / before a pick). */
  picked: ChoiceOut | null;
  /** True when the puzzle resolved because the clock ran out. */
  timedOut: boolean;
  /** Wall-clock (ms) when the choosing phase began; 0 until then. */
  choosingStartedAt: number;
  /** Whole session finished (advanced past the last item). */
  completed: boolean;
}

export type ChoiceAction =
  | { type: "INIT"; items: TrainingItemOut[] }
  | { type: "REVEAL_NEXT" }
  | { type: "START_CHOOSING"; nowMs: number }
  | { type: "PICK"; choice: ChoiceOut; nowMs: number }
  | { type: "TICK"; nowMs: number }
  | { type: "ADVANCE"; nowMs: number }
  | { type: "COMPLETE" };

export interface ChoiceReducerResult {
  state: ChoiceDrillState;
  /** Emitted once when a puzzle resolves (a pick or a timeout). */
  outcome: DrillOutcome | null;
}

const NO_OUTCOME: ChoiceReducerResult["outcome"] = null;

/** Total options to reveal during preview, capped at what the item carries. */
function choiceCount(item: TrainingItemOut | undefined): number {
  return item?.choices?.length ?? 0;
}

export function initChoiceState(items: TrainingItemOut[], index = 0): ChoiceDrillState {
  return {
    index,
    items,
    phase: "preview",
    revealedCount: 0,
    remainingSec: null,
    picked: null,
    timedOut: false,
    choosingStartedAt: 0,
    completed: false,
  };
}

/** A pick is correct iff its kind is the engine's best move. */
export function isCorrectChoice(choice: ChoiceOut | null): boolean {
  return choice?.kind === "best";
}

function freshItemState(
  items: TrainingItemOut[],
  index: number,
  carry: Pick<ChoiceDrillState, "completed">,
): ChoiceDrillState {
  return {
    index,
    items,
    phase: "preview",
    revealedCount: 0,
    remainingSec: null,
    picked: null,
    timedOut: false,
    choosingStartedAt: 0,
    completed: carry.completed,
  };
}

function buildOutcome(
  state: ChoiceDrillState,
  nowMs: number,
  opts: { solved: boolean; timedOut: boolean; pickedSan?: string },
): DrillOutcome {
  return {
    solved: opts.solved,
    tries_used: 1,
    ms_taken: state.choosingStartedAt ? nowMs - state.choosingStartedAt : 0,
    // Log the wrong pick (if any) so analytics mirror the move-drill payload.
    wrong_moves: !opts.solved && opts.pickedSan ? [opts.pickedSan] : [],
    timed_out: opts.timedOut,
  };
}

export function choiceReducer(
  state: ChoiceDrillState,
  action: ChoiceAction,
): ChoiceReducerResult {
  switch (action.type) {
    case "INIT":
      return { state: initChoiceState(action.items, 0), outcome: NO_OUTCOME };

    case "REVEAL_NEXT": {
      if (state.phase !== "preview") return { state, outcome: NO_OUTCOME };
      const total = choiceCount(state.items[state.index]);
      const revealedCount = Math.min(total, state.revealedCount + 1);
      return { state: { ...state, revealedCount }, outcome: NO_OUTCOME };
    }

    case "START_CHOOSING": {
      if (state.phase !== "preview") return { state, outcome: NO_OUTCOME };
      const item = state.items[state.index];
      const total = choiceCount(item);
      return {
        state: {
          ...state,
          phase: "choosing",
          revealedCount: total,
          remainingSec: item?.time_limit_seconds ?? null,
          choosingStartedAt: action.nowMs,
        },
        outcome: NO_OUTCOME,
      };
    }

    case "PICK": {
      if (state.phase !== "choosing") return { state, outcome: NO_OUTCOME };
      const solved = isCorrectChoice(action.choice);
      const next: ChoiceDrillState = {
        ...state,
        phase: "feedback",
        picked: action.choice,
        timedOut: false,
      };
      return {
        state: next,
        outcome: buildOutcome(state, action.nowMs, {
          solved,
          timedOut: false,
          pickedSan: action.choice.uci,
        }),
      };
    }

    case "TICK": {
      if (state.phase !== "choosing" || state.remainingSec == null) {
        return { state, outcome: NO_OUTCOME };
      }
      const remainingSec = state.remainingSec - 1;
      if (remainingSec <= 0) {
        // Out of time — resolve as a failed (timed-out) puzzle.
        return {
          state: { ...state, phase: "feedback", remainingSec: 0, timedOut: true },
          outcome: buildOutcome(state, action.nowMs, { solved: false, timedOut: true }),
        };
      }
      return { state: { ...state, remainingSec }, outcome: NO_OUTCOME };
    }

    case "ADVANCE": {
      const nextIndex = state.index + 1;
      if (nextIndex < state.items.length) {
        return {
          state: freshItemState(state.items, nextIndex, { completed: state.completed }),
          outcome: NO_OUTCOME,
        };
      }
      return { state, outcome: NO_OUTCOME };
    }

    case "COMPLETE":
      return { state: { ...state, completed: true }, outcome: NO_OUTCOME };

    default:
      return { state, outcome: NO_OUTCOME };
  }
}

// ---------------------------------------------------------------------------
// React wrapper. Drives the preview reveal timer, the choosing countdown, and
// the feedback auto-advance; fires host callbacks on resolve + complete.
// ---------------------------------------------------------------------------

export interface UseChoiceDrillOptions {
  items: TrainingItemOut[];
  /** ms each preview arrow stays before the next reveals (default 1200). */
  previewMs?: number;
  /** ms the feedback flash shows before auto-advancing (default 1400). */
  feedbackMs?: number;
  /** Fired once per puzzle when it resolves (pick or timeout). */
  onSolveItem?: (item: TrainingItemOut, outcome: DrillOutcome) => void;
  /** Fired once when the session runs out of items. */
  onComplete?: () => void;
}

export interface UseChoiceDrill {
  current: TrainingItemOut | undefined;
  index: number;
  total: number;
  phase: ChoicePhase;
  revealedCount: number;
  remainingSec: number | null;
  picked: ChoiceOut | null;
  timedOut: boolean;
  completed: boolean;
  /** True once the puzzle has resolved (feedback phase). */
  resolved: boolean;
  /** Submit the user's choice (no-op outside the choosing phase). */
  pick: (choice: ChoiceOut) => void;
}

const now = () => Date.now();

function itemsKey(items: TrainingItemOut[]): string {
  return items.map((it) => it.id).join("|");
}

export function useChoiceDrill(opts: UseChoiceDrillOptions): UseChoiceDrill {
  const { items, previewMs = 1200, feedbackMs = 1400 } = opts;

  const [{ state }, rawDispatch] = useReducer(
    (acc: ChoiceReducerResult, action: ChoiceAction) => choiceReducer(acc.state, action),
    items,
    (init) => ({ state: initChoiceState(init, 0), outcome: NO_OUTCOME }),
  );

  const cbRef = useRef(opts);
  cbRef.current = opts;
  const stateRef = useRef(state);
  stateRef.current = state;

  const dispatch = useCallback((action: ChoiceAction) => rawDispatch(action), []);

  // Emit the resolve callback exactly once per puzzle by re-running the pure
  // reducer to read the outcome side-channel (useReducer can't surface it).
  const resolvedIndexRef = useRef(-1);
  const emit = useCallback(
    (action: ChoiceAction) => {
      const result = choiceReducer(stateRef.current, action);
      if (result.outcome && resolvedIndexRef.current !== stateRef.current.index) {
        resolvedIndexRef.current = stateRef.current.index;
        const item = stateRef.current.items[stateRef.current.index];
        if (item) cbRef.current.onSolveItem?.(item, result.outcome);
      }
      dispatch(action);
    },
    [dispatch],
  );

  // Re-init when a different item list arrives (e.g. the session query resolves
  // after mount). Mirrors useDrillSession's guard so progress isn't clobbered.
  const itemsKeyRef = useRef(itemsKey(items));
  useEffect(() => {
    const key = itemsKey(items);
    if (key === itemsKeyRef.current && state.items.length > 0) return;
    itemsKeyRef.current = key;
    resolvedIndexRef.current = -1;
    dispatch({ type: "INIT", items });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, dispatch]);

  // Preview: reveal arrows one-by-one, then start the choosing phase.
  useEffect(() => {
    if (state.phase !== "preview" || state.items.length === 0) return;
    const total = choiceCount(state.items[state.index]);
    if (state.revealedCount < total) {
      const id = setTimeout(() => dispatch({ type: "REVEAL_NEXT" }), previewMs);
      return () => clearTimeout(id);
    }
    // All revealed — hold one more beat, then open the choosing phase.
    const id = setTimeout(() => dispatch({ type: "START_CHOOSING", nowMs: now() }), previewMs);
    return () => clearTimeout(id);
  }, [state.phase, state.revealedCount, state.index, state.items, previewMs, dispatch]);

  // Choosing: per-second countdown. The reducer no-ops outside the phase.
  useEffect(() => {
    if (state.phase !== "choosing" || state.remainingSec == null) return;
    const id = setInterval(() => emit({ type: "TICK", nowMs: now() }), 1000);
    return () => clearInterval(id);
  }, [state.phase, state.remainingSec, emit]);

  // Feedback: hold the result, then advance (or complete at the end).
  useEffect(() => {
    if (state.phase !== "feedback") return;
    const id = setTimeout(() => {
      const s = stateRef.current;
      const nextIndex = s.index + 1;
      resolvedIndexRef.current = -1;
      if (nextIndex < s.items.length) {
        dispatch({ type: "ADVANCE", nowMs: now() });
      } else {
        dispatch({ type: "COMPLETE" });
        cbRef.current.onComplete?.();
      }
    }, feedbackMs);
    return () => clearTimeout(id);
  }, [state.phase, state.index, feedbackMs, dispatch]);

  const pick = useCallback(
    (choice: ChoiceOut) => emit({ type: "PICK", choice, nowMs: now() }),
    [emit],
  );

  const item = state.items[state.index];
  return {
    current: item,
    index: state.index,
    total: state.items.length,
    phase: state.phase,
    revealedCount: state.revealedCount,
    remainingSec: state.remainingSec,
    picked: state.picked,
    timedOut: state.timedOut,
    completed: state.completed,
    resolved: state.phase === "feedback",
    pick,
  };
}
